import React, { CSSProperties } from 'react';
import { z } from 'zod';

import { BORDER_SHAPE, getBorderStyles } from '../border';

const COLOR_SCHEMA = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/)
  .nullable()
  .optional();

const PADDING_SCHEMA = z
  .object({
    top: z.number(),
    bottom: z.number(),
    right: z.number(),
    left: z.number(),
  })
  .optional()
  .nullable();

const getSpacing = (padding: z.infer<typeof PADDING_SCHEMA>) =>
  padding ? `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px` : undefined;

export const ContainerPropsSchema = z.object({
  style: z
    .object({
      backgroundColor: COLOR_SCHEMA,
      backgroundImage: z.string().optional().nullable(),
      borderColor: COLOR_SCHEMA,
      ...BORDER_SHAPE,
      borderRadius: z.number().optional().nullable(),
      padding: PADDING_SCHEMA,
      margin: PADDING_SCHEMA,
    })
    .optional()
    .nullable(),
});

export type ContainerProps = {
  style?: z.infer<typeof ContainerPropsSchema>['style'];
  children?: React.JSX.Element | React.JSX.Element[] | null;
};

export function Container({ style, children }: ContainerProps) {
  const wStyle: CSSProperties = {
    backgroundColor: style?.backgroundColor ?? undefined,
    backgroundImage: style?.backgroundImage
      ? `url(${JSON.stringify(style.backgroundImage)})`
      : undefined,
    backgroundSize: style?.backgroundImage ? 'cover' : undefined,
    backgroundPosition: style?.backgroundImage ? 'center' : undefined,
    backgroundRepeat: style?.backgroundImage ? 'no-repeat' : undefined,
    ...getBorderStyles(style),
    borderRadius: style?.borderRadius ?? undefined,
    padding: getSpacing(style?.padding),
    margin: getSpacing(style?.margin),
  };
  if (!children) {
    return <div style={wStyle} />;
  }
  return <div style={wStyle}>{children}</div>;
}
